// Libraries
import moment from 'moment'

// Utilities
import isEmpty from './isEmpty'
import { regex } from '../validators/regex'
import { RegexConstants } from '../constants'

const formatter = {
  /**
   * Formats the passed in value to a currency string (supports positive and negative values).
   * @param {String|Number} value - The currency string or number to format.
   * @param {Number} [decimalPrecision] - How many decimal places out to allow in the returned value.
   * @param {Boolean} [replaceRegex] - Whether to apply regex value replacement logic or not.
   * @returns {String} The formatted currency value to display to a user (example: $100.21 or -$10.25).
   */
  currency (value, decimalPrecision = 2, replaceRegex = true) {
    if (isEmpty(value)) {
      return value
    }

    let currencyValue = Number.parseFloat(value).toFixed(decimalPrecision)

    const plusMinus = (currencyValue.indexOf('-') === 0 ? '-' : '')

    if (replaceRegex) {
      currencyValue = currencyValue.replace(RegexConstants.currencyFormatting, '$1,')
    }

    return `${plusMinus}$${currencyValue.replace('-', '')}`
  },

  /**
   * Returns a formatted date string from the passed in moment object (or string but moment is preferred).
   * @param {moment|String} [value] - The date to format.
   * @param {Boolean} [forceLongMonth] - Ensures the full name of the month is used instead of an abbreviation (example: January instead of Jan).
   * @param {Boolean} [longDayNumber] - Ensures that day has 2 digits at all times (example: 09 instead of 9).
   * @returns {String} A formatted date string in the form: (examples: July 9, 2019 or Jul 9, 2019 or July 09, 2019).
   */
  fullShortDate (value, forceLongMonth = false, longDayNumber = false) {
    if (!moment.isMoment(value)) { // Prefer passing in moment so exact parsing format can be specified when converting date string
      value = moment(value)
    }

    if (!moment.isMoment(value) || value.isSameOrBefore(moment('1901/01/01', 'YYYY/MM/DD'))) {
      return ''
    }

    const dayFormat = longDayNumber ? 'DD' : 'D'
    const monthFormat = value.month() === 5 || value.month() === 6 || forceLongMonth ? 'MMMM' : 'MMM'

    return value.format(`${monthFormat} ${dayFormat}, YYYY`)
  },

  /**
   * Formats the passed in card number to a masked version with X characters displayed.
   * @param {String|Number} value - The currency string or number to format.
   * @param {Number} [length] - The number of characters to unmask.
   * @returns {String} The masked card number value to display to a user (example: 1234).
   */
  maskedCard (value, length = 4) {
    if (isEmpty(value)) {
      return ''
    }

    length = length * -1
    return value.slice(length)
  },

  /**
   * Formats the passed in string to a phone number with dashes in between each segment.
   * @param {String} value - The phone number string to format.
   * @returns The formatted phone number (example: 123-456-7890).
   */
  phoneDash (value) {
    if (isEmpty(value) || !regex(value, RegexConstants.partialPhone)) {
      return value
    }

    value = value.replace(RegexConstants.phoneNumberFormatting, '')
    value = value.replace('+1', '')

    if (value.length >= 3 && value.length < 6) {
      value = value.substring(0, 3) + '-' + value.substring(3, 6)
    } else if (value.length >= 6) {
      value = value.substring(0, 3) + '-' + value.substring(3, 6) + '-' + value.substring(6)
    }

    return value
  },

  /**
   * Formats the passed in string to a phone number with parentheses around first 3 digits and a dash in between last two segments.
   * @param {String} value - The phone number string to format.
   * @returns The formatted phone number (example: (123) 456-7890).
   */
  phoneParen (value) {
    if (isEmpty(value)) {
      return value
    }

    value = value.replace(RegexConstants.phoneNumberFormatting, '')
    value = value.replace('+1', '')

    if (value.length === 3) {
      value = '(' + value.substring(0, 3) + ')'
    } else if (value.length === 6) {
      value = '(' + value.substring(0, 3) + ') ' + value.substring(3, 6) + '-'
    } else if (value.length === 10) {
      value = '(' + value.substring(0, 3) + ') ' + value.substring(3, 6) + '-' + value.substring(6)
    }

    return value
  },

  /**
   * Formats the passed in string to sentence case format.
   * @param {String} value - The string to format.
   * @returns The formatted sentence case string (example: This Is Sentence Case).
   */
  sentenceCase (value) {
    if (isEmpty(value)) {
      return ''
    }

    value = value.toString().toLowerCase()

    const valParts = value.split(' ')

    const sentenceCaseVal = []

    for (let i = 0; i < valParts.length; i++) {
      sentenceCaseVal.push(valParts[i].charAt(0).toUpperCase() + valParts[i].slice(1))
    }

    return sentenceCaseVal.join(' ')
  },

  /**
   * Returns a formatted date string from the passed in string.
   * @param {String} [value] - The date to format.
   * @returns {String} A formatted date string in the form: MMMM, D, YYYY (example: May 9, 2019).
   */
  shortMonthDayDate (value) {
    if (!value || value <= new Date(1901, 1, 1)) {
      return ''
    }

    return moment(value).format('MMM D')
  },

  stripPhoneNumber (phoneNumber) {
    return (phoneNumber || '').replace(RegexConstants.phoneNumberFormatting, '')
  },

  onlyNumbers (value) {
    return (value || '').replaceAll(/[^0-9]+/g, '')
  },

  dollarAmountWithDecimal (value, event) {
    if (!value) return

    if (event !== 'blurry' || event.type === 'change') {
      // First step: Make sure input is valid before doing any formatting
      if (
        !value || // Value is null
        value === '$' || // User has no value entered, except for the dollar sign
        value !== value.replaceAll(/[^\d.$,]/g, '') || // Only contains digits, periods, dollar signs, or commas)
        value.replaceAll(/[^$]/g, '').length > 1 || // Number of dollar signs in the value
        value.replaceAll(/[^.]/g, '').length > 1 || // Number of periods in the value
        value.indexOf('$') > 0 // Checks if a dollar sign is misplaced
      ) {
        return value
        // eslint-disable-next-line brace-style
      }

      // Step two: If user hit backspace or delete, don't format
      if (event?.inputType === 'deleteContentForward' || event?.inputType === 'deleteContentBackward') {
        return value
      }
    }

    // Finally: If input is valid, format properly
    const inputNumOnly = value.replaceAll(/[^\d.]/g, '')

    const formattedNum = inputNumOnly.replaceAll(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,').toString()

    if (formattedNum.indexOf('$') > -1) {
      return formattedNum
    }

    return formattedNum
  },

  dollarAmountInteger (amount) {
    if (!amount) return

    return Number.parseInt(String(amount).replaceAll(/[^0-9.]/g, ''))
  },

  addCommas (amount) {
    if (!amount) return

    return String(amount).replaceAll(/[^0-9.]/g, '').replaceAll(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,')
  },

  stripCommas (value) {
    if (isEmpty(value)) return 0

    return Number.parseInt(String(value).replaceAll(/[^0-9.]/g, ''))
  },

  formatDateAsDayCommaDate (date) {
    return moment(date, 'M/D/YYYY').format('dddd, MMMM Do')
  },

  convertDateToSimple (date) {
    return moment(date).format('MM/DD/YYYY')
  },

  convertDateToZulu (incomingDate) {
    // Incoming Date format must be MM/DD/YYYY
    return moment(incomingDate, 'M/D/YYYY').utc().format()
  },

  lettersOnly (input) {
    return input.replace(/[^a-zA-Z]+/g, '')
  },

  toTitleCase (str) {
    const exceptions = ['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on', 'or', 'so', 'the', 'to', 'up', 'yet']

    return str
      .trim()
      .replaceAll(/\s+/g, ' ')
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        if (index === 0 || index === str.trim().split(' ').length - 1 || !exceptions.includes(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1)
        }
        return word
      })
      .join(' ')
  },

  dateFormat (value, event) {
    const incomingDate = event.target.value.replace(' ', '')
    const keyIsDelete = !event.data && event.type === 'input'

    // Guard against keys that are not 0-9 or a slash '/'
    if (keyIsDelete || (event.type !== 'blur' && keystrokeIsInvalid(event.data))) {
      return incomingDate.replaceAll(/[^0-9/]+/g, '')
    }

    let incomingDateSections = incomingDate.split('/')
    const sectionsCompleted = incomingDateSections.length - 1

    // Delete blank sections
    incomingDateSections = deleteBlankSections(incomingDateSections)

    // This doesn't format for delete flow,
    // also must allow for editing without immediate replacement length > 2 && dateIsFilledOut
    if (keyIsDelete || dateIsFilledOut(incomingDateSections)) {
      return incomingDate
    }

    let returnIncomingDate = ''

    incomingDateSections.forEach((section, index) => {
      // Pad day and month with a 0 if length 1
      // If year is 2 digits, determine if it should prepend 20
      if (index === 0 && (section.length === 2 || sectionsCompleted > 0)) {
        returnIncomingDate += section.length < 2 ? `0${section}/` : `${section}/`
      } else if (index === 1 && (section.length === 2 || sectionsCompleted > 1)) {
        returnIncomingDate += section.length < 2 ? `0${section}/` : `${section}/`
      } else if (index === 2) {
        returnIncomingDate += yearConversionFunction(section, event.type !== 'blur')
      } else {
        returnIncomingDate += section
      }
    })

    return returnIncomingDate
  }
}

export default formatter

const deleteBlankSections = (sections) => {
  const newSections = []

  sections.forEach(section => {
    if (section.length !== 0) {
      newSections.push(section)
    }
  })

  return newSections
}

const dateIsFilledOut = (incomingDateSections) => {
  if (incomingDateSections.length <= 2) { return false }

  let sectionsAreFull = false

  incomingDateSections.forEach(section => {
    if (section.length > 2) { sectionsAreFull = true }
  })

  return sectionsAreFull
}

const keystrokeIsInvalid = (data) => {
  if (isNaN(data) && data !== '/') { return true }

  return false
}

const yearConversionFunction = (dateSection) => {
  if (dateSection.length !== 2) {
    return dateSection
  }

  if (dateSection === '20') {
    return dateSection
  }

  return `20${dateSection}`
}
